/**
 * ES Specials index — promotion hub.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import EsSEO from '@/components/seo/EsSEO';
import useSpanishTrackingNumber from '@/hooks/useSpanishTrackingNumber';

const SITE = 'https://fortworthdental.com';

const SPECIALS = [
  {
    title: 'Consulta de Implantes Dentales GRATIS',
    value: 'Valor $475',
    description: 'Consulta inicial con radiografías 3D y plan de tratamiento personalizado — totalmente gratuita para nuevos pacientes considerando implantes dentales.',
    cta: 'Agenda tu consulta',
    ctaRoute: '/es/implantes-dentales',
    color: 'from-amber-500 to-rose-500',
  },
  {
    title: 'Consulta de Invisalign GRATIS',
    value: 'Incluye escaneo digital',
    description: 'Escaneo digital 3D y vista previa de tu sonrisa final — sin costo. Descubre si Invisalign es la opción correcta para ti.',
    cta: 'Agenda tu consulta',
    ctaRoute: '/es/invisalign',
    color: 'from-teal-500 to-emerald-500',
  },
  {
    title: 'Limpieza Dental + Examen + Radiografías — $199',
    value: 'Ahorras más de $150',
    description: 'Especial para nuevos pacientes. Limpieza profesional, examen completo, radiografías digitales y detección de cáncer oral por solo $199.',
    cta: 'Reservar especial',
    ctaRoute: '/es/contacto',
    color: 'from-blue-500 to-teal-500',
  },
  {
    title: 'Emergencia Dental — Examen + Radiografía $99',
    value: 'Atención el mismo día',
    description: '¿Dolor de muela o diente roto? Examen de emergencia con radiografía por solo $99. Citas el mismo día disponibles.',
    cta: 'Llamar ahora',
    ctaRoute: '/es/dentista-de-emergencia',
    color: 'from-rose-500 to-red-500',
  },
  {
    title: 'Consulta LANAP Láser GRATIS',
    value: 'Alternativa a cirugía tradicional',
    description: 'Tratamiento láser aprobado por la FDA para enfermedad de las encías — recuperación más rápida y menos molestias que la cirugía tradicional. Consulta gratis.',
    cta: 'Saber más',
    ctaRoute: '/es/contacto',
    color: 'from-purple-500 to-blue-500',
  },
  {
    title: 'Carillas First Fit — Consulta GRATIS',
    value: 'Sonrisa nueva en 2 horas',
    description: 'La revolucionaria tecnología First Fit te da una sonrisa nueva en solo 2 horas — sin impresiones, sin temporales, sin esperas en el laboratorio dental. Consulta inicial gratuita.',
    cta: 'Agenda tu consulta',
    ctaRoute: '/es/carillas-first-fit',
    color: 'from-pink-500 to-purple-500',
  },
];

const SpanishSpecialsPage = () => {
  const { displayNumber, telLink } = useSpanishTrackingNumber();
  return (
  <>
    <EsSEO
      title="Promociones y Especiales – Fort Worth Dental"
      description="Especiales para nuevos pacientes: consulta de implantes GRATIS, limpieza $199, emergencia dental $99. Aceptamos seguro dental."
      canonical="/es/promociones"
      hreflangEn="/specials"
      schema={{
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        inLanguage: 'es-US',
        url: `${SITE}/es/promociones`,
        name: 'Promociones Dentales',
      }}
    />

    <section className="bg-gradient-to-br from-amber-500 via-rose-500 to-purple-600 text-white py-16">
      <div className="container mx-auto px-4 max-w-5xl">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">Promociones Dentales</h1>
        <p className="text-lg text-white/90 max-w-3xl">
          Aprovecha nuestras ofertas exclusivas para nuevos pacientes. Atención de calidad al precio más accesible en Fort Worth.
        </p>
      </div>
    </section>

    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4 max-w-5xl">
        <div className="grid md:grid-cols-2 gap-6">
          {SPECIALS.map((s) => (
            <div key={s.title} className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow" data-testid={`es-special-${s.ctaRoute}`}>
              <div className={`bg-gradient-to-r ${s.color} text-white p-5`}>
                <span className="text-xs font-bold uppercase tracking-wider bg-white/20 px-2 py-1 rounded-full">{s.value}</span>
                <h2 className="text-xl font-bold mt-2 leading-tight">{s.title}</h2>
              </div>
              <div className="p-6">
                <p className="text-gray-700 mb-5">{s.description}</p>
                <Link
                  to={s.ctaRoute}
                  className="inline-block bg-teal-600 hover:bg-teal-700 text-white font-semibold px-6 py-2.5 rounded-md"
                >
                  {s.cta} →
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="py-12 bg-teal-50">
      <div className="container mx-auto px-4 max-w-3xl text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-3">¿Tienes seguro dental?</h2>
        <p className="text-gray-700 mb-5">
          Aceptamos la mayoría de seguros mayores: Delta Dental, Cigna, Aetna, MetLife, Humana y más. Verificamos tu cobertura sin costo.
        </p>
        <a
          href={telLink}
          className="inline-block bg-teal-600 hover:bg-teal-700 text-white font-semibold px-8 py-3 rounded-md"
        >
          Llamar: {displayNumber}
        </a>
      </div>
    </section>

    {/* Patient testimonial */}
    <section className="py-16 bg-white" data-testid="es-specials-testimonial">
      <div className="container mx-auto px-4 max-w-3xl">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Lo Que Dicen Nuestros Pacientes</h2>
        <blockquote className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg">
          <p className="text-gray-700 italic leading-relaxed mb-3">
            "Aproveché la promoción de $199 y me ahorré mucho dinero. La limpieza fue completa, el examen detallado y todo el personal me explicó todo en español. ¡Muy recomendado!"
          </p>
          <footer className="text-sm font-semibold text-gray-900">— Carlos R., Arlington</footer>
        </blockquote>
      </div>
    </section>

    {/* Final CTA */}
    <section className="py-16 bg-teal-600 text-white" data-testid="es-specials-cta">
      <div className="container mx-auto px-4 text-center">
        <h2 className="text-3xl sm:text-4xl font-bold mb-4">¿Listo para Aprovechar Estas Ofertas?</h2>
        <p className="text-xl mb-8">Espacios limitados — agenda tu cita hoy y empieza a ahorrar.</p>
        <div className="flex flex-col md:flex-row justify-center items-center gap-4 md:gap-6">
          <a href={telLink} className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100">
            Llamar a {displayNumber}
          </a>
          <Link to="/es/contacto" className="bg-yellow-500 text-yellow-900 px-8 py-4 rounded-lg text-lg font-bold hover:bg-yellow-400">
            Reservar Cita
          </Link>
        </div>
      </div>
    </section>
  </>
  );
};

export default SpanishSpecialsPage;
